<script setup lang="ts">
import { Product } from "~/utils/Product";
import {useToast} from "primevue/usetoast";
import { addToCart } from "~/utils/Cart";

const amount = ref(1)
const props = defineProps<{ product: Product, skeleton?:Boolean}>();
const toast = useToast()

</script>

<template>
  <Toast>
    <template #message="slotProps">
      <div style="width:100%">
        {{slotProps.message.detail}}
        <div @click.stop="router.push('/shop/request')" style="text-decoration: underline;cursor:pointer">
          zur Anfrage
        </div>
      </div>
    </template>
  </Toast>
  <article class="product-card product-card--skeleton" v-if="skeleton==true">
    <Skeleton height="15rem"></Skeleton>
    <div class="product-card__skeleton-content">
      <Skeleton width="45%" height="0.8rem"></Skeleton>
      <Skeleton width="80%" height="1.4rem"></Skeleton>
      <Skeleton width="35%" height="1rem"></Skeleton>
    </div>
  </article>
  <article class="product-card" v-else>
    <NuxtLink :to="`/shop/product/${product.slug}`" class="product-card__link" :aria-label="`${product.Name} ansehen`">
      <div class="product-card__image">
        <NuxtImg
          v-if="product.images[0]?.rel_url"
          provider="strapi"
          format="webp"
          :class="{imageCover:!product.transparentImage}"
          :src="product.images[0].rel_url"
          loading="lazy"
          :alt="product.images[0].alt || product.Name"
        />
        <div v-else class="product-card__placeholder"><i class="pi pi-image" aria-hidden="true"></i></div>
      </div>
      <div class="product-card__content">
        <div v-if="product.badges?.length" class="product-card__badges">
          <span v-for="badge in product.badges.slice(0, 3)" :key="badge.label" :class="`product-badge product-badge--${badge.color}`">{{ badge.label }}</span>
        </div>
        <h2>{{ product.Name }}</h2>
        <div class="product-card__price-row">
          <div>
            <p v-if="product.setPrice && product.setPrice > product.Price" class="previous-price">statt {{ product.setPrice.toLocaleString("de-AT", {style: "currency", currency: "EUR"}) }}</p>
            <p class="price">
              <template v-if="product.Price != 0">{{ product.Price.toLocaleString("de-AT", {style: "currency", currency: "EUR"}) }}</template>
              <template v-else>Preis auf Anfrage</template>
            </p>
          </div>
          <span class="details-link">Details <i class="pi pi-arrow-right" aria-hidden="true"></i></span>
        </div>
      </div>
    </NuxtLink>
    <div class="product-card__quick-add">
      <label :for="`product-amount-${product.id}`">Anzahl</label>
      <div>
        <InputNumber
          v-model="amount"
          :inputId="`product-amount-${product.id}`"
          showButtons
          buttonLayout="horizontal"
          :min="1"
          :pt="{
            incrementButton: { 'aria-label': 'Anzahl erhöhen' },
            decrementButton: { 'aria-label': 'Anzahl verringern' },
          }"
        />
        <Button label="Hinzufügen" icon="pi pi-plus" @click="addToCart(amount,product,toast)" />
      </div>
    </div>
  </article>
</template>
<style scoped lang="scss">
.product-card{
  min-width: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid $public-border;
  border-radius: $public-radius-card;
  color: white;
  background: #191a1d;
  transition: border-color 180ms $public-ease, background-color 180ms $public-ease;

  &:hover{
    border-color: rgba(213, 150, 69, 0.38);
    background: #1c1d20;
  }
}

.product-card__link{
  flex: 1;
  color: inherit;
  text-decoration: none;

  &:focus-visible{
    outline: 2px solid #E39737;
    outline-offset: -3px;
  }
}

.product-card__image{
  height: 245px;
  overflow: hidden;
  background: #252629;

  img{
    width: 100%;
    height: 100%;
    padding: 1rem;
    object-fit: contain;
    background: $imgBackground;
    transition: transform 280ms $public-ease;

    &.imageCover{
      padding: 0;
      object-fit: cover;
      background: none;
    }
  }

  .product-card:hover & img{
    transform: scale(1.015);
  }
}

.product-card__placeholder{
  height: 100%;
  display: grid;
  place-items: center;
  color: rgb(120 117 115);
  font-size: 2rem;
}

.product-card__content{
  padding: 1.25rem;

  h2{
    min-height: 2.6em;
    margin: 0.75rem 0 0;
    font-size: 1.15rem;
    font-weight: 600;
    line-height: 1.3;
  }
}

.product-card__badges{
  min-height: 1.5rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.product-badge{
  padding: 0.28rem 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 999px;
  color: rgb(214 211 209);
  background: rgba(255, 255, 255, 0.055);
  font-size: 0.68rem;
  font-weight: 650;
}

.product-badge--pax{
  border-color: rgba(80, 157, 210, 0.35);
  background: rgba(62, 127, 173, 0.2);
}

.product-badge--feature{
  border-color: rgba(227, 151, 55, 0.35);
  background: rgba(227, 151, 55, 0.13);
}

.product-card__price-row{
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  justify-content: space-between;
  margin-top: 1.2rem;
}

.previous-price{
  margin: 0 0 0.15rem;
  color: rgb(120 117 115);
  font-size: 0.72rem;
  text-decoration: line-through;
}

.price{
  margin: 0;
  font-size: 1.08rem;
  font-weight: 700;
}

.details-link{
  display: inline-flex;
  gap: 0.4rem;
  align-items: center;
  color: $public-accent;
  font-size: 0.8rem;
  font-weight: 700;
}

.product-card__quick-add{
  padding: 1rem 1.25rem 1.25rem;
  border-top: 1px solid rgba(255, 255, 255, 0.07);

  > label{
    display: block;
    margin-bottom: 0.4rem;
    color: rgb(168 162 158);
    font-size: 0.7rem;
    font-weight: 650;
  }

  > div{
    display: grid;
    grid-template-columns: 112px minmax(0, 1fr);
    gap: 0.65rem;
  }

  :deep(.p-inputnumber-input){
    width: 52px;
    text-align: center;
  }

  :deep(.p-button){
    padding-inline: 0.75rem;
    border-color: $public-accent;
    color: #151515;
    background: $public-accent;
    font-weight: 700;

    &:hover{
      border-color: $public-accent-hover;
      background: $public-accent-hover;
    }
  }
}

.product-card--skeleton{
  padding: 0.9rem;
  gap: 1rem;
}

.product-card__skeleton-content{
  display: grid;
  gap: 0.8rem;
}

@media (max-width: 639px){
  .product-card__image{
    height: 230px;
  }

  .product-card__content h2{
    min-height: 0;
  }
}
</style>
